import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import type { AssistantReferralDraft } from '../contract'

const NOT_SET = 'Not set yet'

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 110 }}>
      {label}
    </Typography>
    <Typography
      variant="caption"
      color={value === NOT_SET ? 'text.disabled' : 'text.primary'}
      sx={{ flex: 1, overflowWrap: 'anywhere' }}
    >
      {value}
    </Typography>
  </Box>
)

const clientName = (client: AssistantReferralDraft['client']) =>
  [client.firstName, client.lastName].filter(Boolean).join(' ') || NOT_SET

/**
 * The referral exactly as it would be posted. Shared by the draft result and
 * (later) the post-referral approval card. All values are plain text.
 */
export const AssistantReferralPreview = ({
  draft,
}: {
  draft: AssistantReferralDraft
}) => (
  <Box>
    <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
      {draft.title || 'Untitled referral'}
    </Typography>
    <Typography
      variant="body2"
      sx={{ my: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
    >
      {draft.description}
    </Typography>
    <Row label="Practice area" value={draft.practiceArea ?? NOT_SET} />
    <Row label="Location" value={draft.jurisdiction ?? NOT_SET} />
    <Row label="Case stage" value={draft.processStage ?? NOT_SET} />
    <Row
      label="Incident date"
      value={
        draft.incidentDate
          ? dayjs(draft.incidentDate).format('MMM D, YYYY')
          : NOT_SET
      }
    />
    <Row label="Referral fee" value={`${draft.fee}%`} />
    <Row label="Client" value={clientName(draft.client)} />
  </Box>
)

interface AssistantReferralDraftCardProps {
  draft: AssistantReferralDraft
  missing: string[]
}

/** A draft built from the user's words. Nothing is posted from this card. */
const AssistantReferralDraftCard = ({
  draft,
  missing,
}: AssistantReferralDraftCardProps) => (
  <Box
    data-testid="assistant-referral-draft"
    sx={{
      p: 2,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
      borderStyle: 'dashed',
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="overline" color="text.secondary">
      Draft referral · not posted
    </Typography>
    <AssistantReferralPreview draft={draft} />
    {missing.length > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="warning.dark" component="p">
          Still needed before it can be posted:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {missing.map(item => (
            <Typography key={item} component="li" variant="caption">
              {item}
            </Typography>
          ))}
        </Box>
      </Box>
    )}
  </Box>
)

export default AssistantReferralDraftCard
